import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, ChevronDown, ChevronUp, Newspaper, Info, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function scoreColor(s) {
  if (s == null) return '#64748b';
  return s >= 7 ? '#16C784' : s >= 4 ? '#f59e0b' : '#ef4444';
}

function GradientBar({ score, trackColor }) {
  if (score == null) return (
    <div className="w-full h-2 rounded-full" style={{ backgroundColor: trackColor || 'rgba(255,255,255,0.06)' }} />
  );
  const pct = (score / 10) * 100;
  return (
    <div className="relative w-full h-2 rounded-full overflow-hidden"
      style={{ background: 'linear-gradient(to right, #ef4444, #f59e0b, #16C784)' }}>
      <div className="absolute top-0 h-full w-1 bg-white rounded-full shadow"
        style={{ left: `${pct}%`, transform: 'translateX(-50%)' }} />
    </div>
  );
}

function LayerBar({ label, score, detail, state, colors }) {
  const color = scoreColor(score);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: colors.secondaryText }}>{label}</span>
        <div className="flex items-center gap-2">
          {state && (
            <span className="text-[13px] font-medium" style={{ color }}>{state}</span>
          )}
          <span className="text-xs font-bold" style={{ color }}>
            {score != null ? score.toFixed(1) : '—'}
          </span>
        </div>
      </div>
      <GradientBar score={score} trackColor={colors.progressTrack} />
      {detail && (
        <p className="text-[13px]" style={{ color: '#475569' }}>{detail}</p>
      )}
    </div>
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function EdgeScore({ symbol, colors }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showHeadlines, setShowHeadlines] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    const clean = symbol.replace('.NS', '');
    axios.get(`${API_URL}/stocks/${clean}/edge-score`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) {
    return (
      <div className="app-card rounded-xl p-4 space-y-4 animate-pulse"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
        <div className="h-5 w-32 rounded" style={{ backgroundColor: colors.inputBg }} />
        <div className="h-10 w-24 rounded" style={{ backgroundColor: colors.inputBg }} />
        <div className="h-2 w-full rounded-full" style={{ backgroundColor: colors.inputBg }} />
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-6 rounded" style={{ backgroundColor: colors.inputBg }} />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.overall_score == null) return null;

  const overall = data.overall_score;
  const color = scoreColor(overall);
  const { layers, decision, risk_flags, headlines, analysis } = data;

  const decisionColors = {
    'Bullish Setup':  { bg: `${colors.profit}20`, text: colors.profit },
    'Moderate Setup': { bg: `${colors.profit}15`, text: colors.profit },
    'Strong Setup':   { bg: '#7c3aed20',          text: '#7c3aed' },
    'Neutral':        { bg: `${colors.accent}20`, text: colors.accent },
    'Weak Setup':     { bg: `${colors.loss}20`,   text: colors.loss },
  };
  const dc = decision ? (decisionColors[decision.action] || decisionColors['Neutral']) : null;
  const riskColor = decision?.risk === 'LOW' ? colors.profit
    : decision?.risk === 'HIGH' ? colors.loss : colors.accent;

  // Layer detail strings
  const techDetail = layers.technical?.state
    ? `Score ${layers.technical.raw}/100`
    : null;
  const sentDetail = layers.sentiment
    ? `${layers.sentiment.label} · ${layers.sentiment.articles} articles`
    : null;
  const fundDetail = layers.fundamental?.state
    ? `Score ${layers.fundamental.raw}/100`
    : null;
  const pattDetail = layers.patterns?.patterns?.length
    ? `${layers.patterns.patterns.length} active pattern${layers.patterns.patterns.length > 1 ? 's' : ''}`
    : null;

  return (
    <div className="app-card rounded-xl p-4"
      style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5" style={{ color: colors.accent }} />
          <h2 className="font-semibold" style={{ color: colors.primaryText }}>Edge Score</h2>
          <button onClick={() => setShowInfo(v => !v)} className="opacity-50 hover:opacity-100 transition-opacity">
            <Info className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
          </button>
        </div>
        {decision && (
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-lg text-xs font-bold"
              style={{ backgroundColor: dc.bg, color: dc.text }}>
              {decision.action}
            </span>
            <span className="px-2 py-1 rounded-lg text-[13px] font-medium"
              style={{ backgroundColor: `${riskColor}20`, color: riskColor }}>
              {decision.risk}
            </span>
          </div>
        )}
      </div>

      {/* ── Info Panel ────────────────────────────────────────────── */}
      {showInfo && (
        <div className="rounded-lg p-3 mb-4 text-[14px] leading-relaxed space-y-2"
          style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-xs" style={{ color: colors.primaryText }}>How Edge Score works</span>
            <button onClick={() => setShowInfo(false)}><X className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} /></button>
          </div>
          <p>The overall score (0-10) is a weighted blend of four independent layers:</p>
          <div className="space-y-1">
            <p><span className="font-medium" style={{ color: colors.primaryText }}>Technical (35%)</span> — RSI, MACD, EMA crossovers, Bollinger Bands, Supertrend, ADX, and volume analysis from daily market data.</p>
            <p><span className="font-medium" style={{ color: colors.primaryText }}>Fundamental (25%)</span> — P/E ratio, ROE, debt-to-equity, promoter holding, and revenue growth from quarterly financials.</p>
            <p><span className="font-medium" style={{ color: colors.primaryText }}>Sentiment (20%)</span> — Market and sector news scored via VADER sentiment analysis with 24h time-decay. Blends overall market mood with sector-specific signal.</p>
            <p><span className="font-medium" style={{ color: colors.primaryText }}>Patterns (20%)</span> — Active chart patterns (double bottom/top, H&amp;S, triangles, flags, support/resistance) detected by the pattern scanner.</p>
          </div>
          <p>The <span className="font-medium" style={{ color: colors.primaryText }}>signal badge</span> (Bullish Setup / Neutral / Weak Setup) comes from a rule-based engine that evaluates entry conditions, risk level, and cross-signal agreement. For educational purposes only — not investment advice.</p>
          <p className="opacity-60">Scores update every 10 minutes during market hours. For educational purposes only.</p>
        </div>
      )}

      {/* ── Overall Score + Gradient ─────────────────────────────── */}
      <div className="flex items-end gap-3 mb-1">
        <span className="text-4xl font-bold leading-none" style={{ color }}>
          {overall.toFixed(1)}
        </span>
        <span className="text-sm font-medium mb-1" style={{ color: '#64748b' }}>/10</span>
        {decision?.entry && (
          <span className="text-[13px] px-2 py-0.5 rounded ml-auto"
            style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
            {decision.entry}
          </span>
        )}
      </div>
      <GradientBar score={overall} trackColor={colors.progressTrack} />
      <div className="flex justify-between mt-1 mb-4">
        <span className="text-[12px]" style={{ color: '#64748b' }}>Bearish</span>
        <span className="text-[12px]" style={{ color: '#64748b' }}>Bullish</span>
      </div>

      {/* ── 4 Layer Bars ─────────────────────────────────────────── */}
      <div className="space-y-3 mb-4">
        <LayerBar label="Technical" score={layers.technical?.score}
          state={layers.technical?.state} detail={techDetail} colors={colors} />
        <LayerBar label="Sentiment" score={layers.sentiment?.score}
          state={layers.sentiment?.label} detail={sentDetail} colors={colors} />
        <LayerBar label="Fundamental" score={layers.fundamental?.score}
          state={layers.fundamental?.state} detail={fundDetail} colors={colors} />
        <LayerBar label="Patterns" score={layers.patterns?.score}
          detail={pattDetail} colors={colors} />
      </div>

      {/* ── Market Sentiment Context ─────────────────────────────── */}
      {(data.market_mood || data.sentiment_source) && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg mb-3"
          style={{ backgroundColor: colors.inputBg }}>
          <span className="text-[11px]" style={{ color: colors.secondaryText }}>Market Mood</span>
          <div className="flex items-center gap-2">
            {data.market_mood && (() => {
              const moodColor = data.market_mood === 'RISK_ON' ? '#16C784'
                : data.market_mood === 'RISK_OFF' ? '#EF4444' : '#8b5cf6';
              return (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${moodColor}18`, color: moodColor }}>
                  {data.market_mood.replace('_', ' ')}
                </span>
              );
            })()}
            {data.sentiment_source === 'proxy' && (
              <span className="text-[10px]" style={{ color: colors.secondaryText }}>proxy</span>
            )}
          </div>
        </div>
      )}

      {/* ── Risk Flags ───────────────────────────────────────────── */}
      {risk_flags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {risk_flags.map((f, i) => (
            <span key={i} className="text-[13px] px-2 py-0.5 rounded"
              style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
              ⚠ {f}
            </span>
          ))}
        </div>
      )}

      {/* ── Expandable: Headlines ────────────────────────────────── */}
      {headlines?.length > 0 && (
        <div className="border-t pt-2 mt-2" style={{ borderColor: colors.border }}>
          <button onClick={() => setShowHeadlines(v => !v)}
            className="flex items-center gap-1 text-xs font-medium w-full"
            style={{ color: colors.accent }}>
            <Newspaper className="w-3.5 h-3.5" />
            News Headlines ({headlines.length})
            {showHeadlines ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
          </button>
          {showHeadlines && (
            <div className="mt-2 space-y-1.5">
              {headlines.map((h, i) => {
                const hColor = h.sentiment === 'positive' ? colors.profit
                  : h.sentiment === 'negative' ? colors.loss : colors.secondaryText;
                return (
                  <div key={i} className="flex items-start gap-2 text-[14px]">
                    <span className="shrink-0 mt-0.5" style={{ color: hColor }}>●</span>
                    <span style={{ color: colors.secondaryText }}>{h.title}</span>
                    <span className="shrink-0 ml-auto text-[12px]" style={{ color: '#475569' }}>
                      {timeAgo(h.time)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Expandable: Analysis Details ─────────────────────────── */}
      {analysis && (
        <div className="border-t pt-2 mt-2" style={{ borderColor: colors.border }}>
          <button onClick={() => setShowAnalysis(v => !v)}
            className="flex items-center gap-1 text-xs font-medium w-full"
            style={{ color: colors.accent }}>
            {showAnalysis ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Analysis Details
          </button>
          {showAnalysis && (
            <div className="mt-2 space-y-1">
              {analysis.positives?.map((p, i) => (
                <div key={`p-${i}`} className="flex items-start gap-2 text-xs" style={{ color: colors.profit }}>
                  <span className="mt-0.5 shrink-0">+</span><span>{p}</span>
                </div>
              ))}
              {analysis.warnings?.map((w, i) => (
                <div key={`w-${i}`} className="flex items-start gap-2 text-xs" style={{ color: '#f59e0b' }}>
                  <span className="mt-0.5 shrink-0">!</span><span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Footer ───────────────────────────────────────────────── */}
      <p className="text-[13px] mt-3" style={{ color: '#334155' }}>
        For educational purposes only. Not financial advice.
      </p>
    </div>
  );
}
